import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, Droplets, Anchor, Diamond, Sun, Layers, Lock, Send } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import commercialHero from "@/assets/commercial-hero.webp";
import usePageSEO from "@/hooks/usePageSEO";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import PageFAQ from "@/components/PageFAQ";
import { commercialFAQs } from "@/data/faqData";
import FullServiceCallout from "@/components/FullServiceCallout";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.6, ease: [.22, 1, .36, 1] as [number, number, number, number] } }),
};

const subfloorCards = [
  {
    icon: Building2,
    title: "Multi-Story Pump Trucks",
    text: "For massive commercial footprints, traditional mixing simply won't cut it. We deploy large-scale pump trucks capable of delivering thousands of pounds of self-leveling compound across multiple stories. Ensuring perfectly flat tolerances in a fraction of the time.",
    highlight: "Pump Trucks",
  },
  {
    icon: Droplets,
    title: "Advanced Moisture Mitigation",
    text: "Commercial concrete slabs are notorious for high moisture vapor emission rates. We execute advanced quantitative testing and deploy commercial-grade epoxy vapor barriers. The kind of science-driven prep work that prevents catastrophic failures years down the road.",
    highlight: "MVER",
  },
  {
    icon: Anchor,
    title: "Maritime & Defense Engineering",
    text: "We've engineered flooring systems for polar tankers and military defense vessels. This requires ultra-flexible underlayments that bond to steel decks and absorb extreme structural flexion while at sea. A level of precision most contractors will never encounter.",
    highlight: "polar tankers",
  },
];

const surfaceCards = [
  {
    icon: Diamond,
    title: "Ceramic Crystal Sealers",
    text: "For high-traffic wood floors, we apply advanced finishes like WearMax that embed 11-sided ceramic crystals directly into the sealer. The result is an impenetrable wear layer that prevents walking paths and wear-through. Even in the busiest retail environments.",
    highlights: ["WearMax", "ceramic crystals"],
  },
  {
    icon: Sun,
    title: "Instant UV-Cured Finishes",
    text: "Downtime costs businesses money. We combine our dust-controlled sanding systems with instant UV-cured hardwax oils. The moment our commercial UV light hits the finish, it's 100% cured. Lower VOCs, zero wait time, and immediate foot traffic.",
    highlights: ["UV-cured", "UV light"],
  },
  {
    icon: Layers,
    title: "Impregnated Wood & Commercial Vinyl",
    text: "We supply and install factory-finished, acrylic-impregnated hardwoods where liquid acrylic is forced through the entire wear layer, hardening the wood itself. For resilient applications, we specify commercial vinyls with diamond-hardened technology like Armstrong Diamond 10 for ultimate scratch resistance.",
    highlights: ["acrylic-impregnated", "Armstrong Diamond 10"],
  },
];

const portfolioItems = [
  "High-end luxury retail flagships (Troy/Somerset & Lake Orion).",
  "Major technology hub campuses (Ann Arbor).",
  "Defense contractor facilities and maritime military vessels (Louisiana & Troy).",
  "Corporate high-rises, strip malls, and hospitality venues.",
];

function highlightTerms(text: string, terms: string[]) {
  let result = text;
  terms.forEach((term) => {
    result = result.replace(
      new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"),
      `<strong class="text-[hsl(var(--gold-300))] font-semibold">$1</strong>`
    );
  });
  return result;
}

const scopeOptions = ["Material Supply Only", "Supply & Install", "Leveling & Prep Only"];

const Commercial = () => {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", location: "", scope: "", details: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    window.location.href = `mailto:info@1woodfloors.com?subject=Commercial Inquiry from ${form.company || form.name}&body=${encodeURIComponent(
      `Name: ${form.name}\nCompany: ${form.company}\nEmail: ${form.email}\nPhone: ${form.phone}\nLocation: ${form.location}\nScope: ${form.scope}\n\nDetails:\n${form.details}`
    )}`;
  };

  usePageSEO({
    title: "Commercial Flooring Installation | Licensed Builder",
    description: "Commercial hardwood flooring installation contractor — tech campuses, retail flagships, hospitality. Licensed Michigan Builder, NWFA standards.",
    path: "/commercial",
    // Service schema baked into static HTML via generate-seo-html.mjs
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>

      {/* ── Hero ── */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <PageBreadcrumb variant="overlay" items={[
          { label: "Home", to: "/" },
          { label: "Services" },
          { label: "Commercial" },
        ]} />
        <div className="absolute inset-0">
          <img width={1600} height={1067} loading="eager" decoding="async" fetchPriority="high" src={commercialHero} alt="Large commercial interior with hardwood flooring" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--neutral-950))]/90 via-[hsl(var(--neutral-950))]/70 to-[hsl(var(--neutral-950))]/50" />
        </div>
        <div className="relative z-10 max-w-[900px] mx-auto px-5 md:px-8 text-center py-24">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="text-primary tracking-[.18em] uppercase text-xs font-semibold mb-4">
            Commercial Division
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground leading-[1.05] mb-6">
            Commercial Hardwood & High Performance Flooring for Michigan
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="text-primary-foreground/60 text-base md:text-lg max-w-[680px] mx-auto leading-relaxed mb-8">
            From luxury retail flagships and tech campus lobbies to military maritime vessels. We engineer advanced moisture mitigation, multi-story leveling, and ultra-durable surface technologies for projects of any scale.
          </motion.p>
          <motion.a
            href="#commercial-contact"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="inline-block bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[hsl(var(--gold-400))] transition-colors"
          >
            Request a Commercial Bid
          </motion.a>
        </div>
      </section>

      {/* ── Subfloor & Moisture Engineering ── */}
      <section className="bg-[hsl(var(--neutral-950))] py-20 md:py-28 px-5 md:px-8">
        <div className="max-w-[1100px] mx-auto">
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-primary tracking-[.18em] uppercase text-xs font-semibold mb-3">
            Large-Scale Engineering
          </motion.p>
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0.5} className="font-display text-3xl md:text-5xl font-bold text-primary-foreground mb-4">
            The Foundation of Commercial <span className="italic text-[hsl(var(--gold-300))]">Success.</span>
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="text-primary-foreground/50 max-w-[680px] text-sm md:text-base leading-relaxed mb-12">
            Commercial spaces demand an entirely different caliber of subfloor preparation and moisture testing. We bring the heavy equipment, the material science, and the NWFA expertise to prevent the kind of catastrophic failures that shut businesses down.
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {subfloorCards.map((card, i) => (
              <motion.div
                key={card.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i + 1.5}
                className="group border border-primary-foreground/[.08] rounded-2xl p-7 bg-primary-foreground/[.02] hover:border-primary/30 hover:bg-primary/[.04] transition-all duration-500 hover:-translate-y-1"
              >
                <card.icon className="w-8 h-8 text-primary mb-5" strokeWidth={1.4} />
                <h3 className="font-display text-xl font-semibold text-primary-foreground mb-3">{card.title}</h3>
                <p
                  className="text-primary-foreground/45 text-[13px] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: highlightTerms(card.text, [card.highlight]) }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Surface Technologies ── */}
      <section className="bg-background py-20 md:py-28 px-5 md:px-8">
        <div className="max-w-[1100px] mx-auto" style={{ maxWidth: 1100 }}>
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-primary tracking-[.18em] uppercase text-xs font-semibold mb-3">
            Advanced Finishes
          </motion.p>
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0.5} className="font-display text-3xl md:text-5xl font-bold text-foreground mb-4">
            Extreme Durability & <span className="italic text-primary">Minimal Downtime.</span>
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="text-muted-foreground max-w-[680px] text-sm md:text-base leading-relaxed mb-12">
            Commercial traffic requires specialized chemistry. We specify and apply finishes that standard contractors cannot access or execute.
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {surfaceCards.map((card, i) => (
              <motion.div
                key={card.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i + 1.5}
                className="group border border-border rounded-2xl p-7 bg-card hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-500 hover:-translate-y-1"
              >
                <card.icon className="w-8 h-8 text-primary mb-5" strokeWidth={1.4} />
                <h3 className="font-display text-xl font-semibold text-foreground mb-3">{card.title}</h3>
                <p
                  className="text-muted-foreground text-[13px] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: highlightTerms(card.text, card.highlights) }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Confidential Portfolio ── */}
      <section className="bg-[hsl(var(--neutral-950))] py-20 md:py-28 px-5 md:px-8">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <Lock className="w-7 h-7 text-primary mx-auto mb-5" strokeWidth={1.4} />
          </motion.div>
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0.5} className="font-display text-3xl md:text-5xl font-bold text-primary-foreground mb-4">
            Trusted by the World's Most <span className="italic text-[hsl(var(--gold-300))]">Demanding Environments.</span>
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="text-primary-foreground/50 text-sm md:text-base leading-relaxed mb-8 max-w-[600px] mx-auto">
            We respect client confidentiality, but our work spans the nation's most rigorous sectors. Our NWFA Certified crews have successfully engineered and installed flooring for:
          </motion.p>
          <motion.ul initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1.5} className="text-left max-w-[520px] mx-auto space-y-3">
            {portfolioItems.map((item) => (
              <li key={item} className="flex items-start gap-3 text-primary-foreground/60 text-[13px] md:text-sm leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                {item}
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ── Commercial Contact Form ── */}
      <section id="commercial-contact" className="bg-background py-20 md:py-28 px-5 md:px-8">
        <div className="max-w-[720px] mx-auto">
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-primary tracking-[.18em] uppercase text-xs font-semibold mb-3 text-center">
            Commercial Inquiries
          </motion.p>
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0.5} className="font-display text-3xl md:text-5xl font-bold text-foreground mb-4 text-center">
            Partner with <span className="italic text-primary">1 Wood Floors.</span>
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="text-muted-foreground text-sm md:text-base leading-relaxed mb-10 text-center max-w-[560px] mx-auto">
            Whether you require direct material supply for your own crews, or our full service installation and leveling teams, backed by Four NWFA Certifications, contact our commercial division below.
          </motion.p>

          <motion.form
            onSubmit={handleSubmit}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={1.5}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <input
              required
              placeholder="Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="col-span-1 h-12 rounded-xl border border-input bg-background px-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              placeholder="Company Name"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="col-span-1 h-12 rounded-xl border border-input bg-background px-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              required
              type="email"
              placeholder="Email *"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="col-span-1 h-12 rounded-xl border border-input bg-background px-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              required
              type="tel"
              placeholder="Phone *"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="col-span-1 h-12 rounded-xl border border-input bg-background px-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              placeholder="Project Location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="col-span-1 h-12 rounded-xl border border-input bg-background px-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <select
              required
              value={form.scope}
              onChange={(e) => setForm({ ...form, scope: e.target.value })}
              className="col-span-1 h-12 rounded-xl border border-input bg-background px-4 text-sm text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring appearance-none"
            >
              <option value="" disabled>Project Scope *</option>
              {scopeOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <textarea
              placeholder="Project Details: Tell us about the space, square footage, timeline, and any subfloor concerns."
              value={form.details}
              onChange={(e) => setForm({ ...form, details: e.target.value })}
              rows={5}
              className="col-span-1 md:col-span-2 rounded-xl border border-input bg-background px-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
            <div className="col-span-1 md:col-span-2 flex justify-center mt-2">
              <button
                type="submit"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-10 py-3.5 rounded-full text-sm font-semibold hover:bg-[hsl(var(--gold-400))] transition-colors"
              >
                <Send className="w-4 h-4" />
                Submit Commercial Inquiry
              </button>
            </div>
          </motion.form>
        </div>
      </section>

        <FullServiceCallout variant="commercial" />
        <PageFAQ suppressJsonLd faqs={commercialFAQs} heading={<>Commercial flooring questions <em className="italic font-normal text-primary">answered.</em></>} subheading="Commercial FAQs" />
      </main>
      <SiteFooter />
    </div>
  );
};

export default Commercial;
